import React, { useEffect, useRef, useState } from 'react';
import { Camera, Send as SendIcon, Square as StopIcon, Save } from 'lucide-react';
import { PlayFab } from '../../app/services/PlayFab';
import { useSSEChat } from '../../hooks/useSSEChat';

export default function ChatPage() {
  const [messages, setMessages] = useState<{ role: 'ai' | 'user'; text: string; imageDataUrl?: string }[]>([
    { role: 'ai', text: 'Hey! Ready to train? I can plan your push day or estimate calories from a photo.' },
  ]);
  const [input, setInput] = useState('');
  const [functionUrl, setFunctionUrl] = useState('');
  const { text, loading, error, start, stop } = useSSEChat(functionUrl || '');
  const [toast, setToast] = useState<string | null>(null);
  const prevLoadingRef = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<{ dataUrl: string; mime: string; size: number; name?: string } | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const nearBottomRef = useRef<boolean>(true);
  const rafRef = useRef<number | null>(null);

  function scrollToBottom() {
    const el = listRef.current;
    if (!el) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      try { el.scrollTop = el.scrollHeight; } catch {}
    });
  }

  function escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function markdownToHtml(md: string): string {
    if (!md) return '';
    const lines = md.split(/\r?\n/);
    let i = 0;
    let html = '';
    let paragraph: string[] = [];

    function flushParagraph() {
      if (paragraph.length > 0) {
        const text = escapeHtml(paragraph.join('\n')).replace(/\n/g, '<br/>');
        html += `<p>${text}</p>`;
        paragraph = [];
      }
    }

    while (i < lines.length) {
      const l = lines[i];
      const t = l.trim();
      const isTableRow = t.startsWith('|') && t.indexOf('|', 1) > 0;
      const next = (j: number) => (i + j < lines.length ? lines[i + j].trim() : '');
      const isSep = (s: string) => /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(s || '');
      const isHeaderWithoutLeadingPipe = (s: string) => {
        const barCount = (s.match(/\|/g) || []).length;
        if (barCount < 2) return false;
        if (isSep(s)) return false;
        return true;
      };

      // Accept GitHub-style tables: header may or may not start with '|', but the next line must be the alignment separator
      if ((isTableRow && (isSep(next(1)) || next(1).startsWith('|'))) || (isHeaderWithoutLeadingPipe(t) && isSep(next(1)))) {
        // Parse table block (tolerant to missing leading pipes)
        flushParagraph();
        const headerLine = t;
        if (isSep(next(1))) {
          i++; // move to separator
          i++; // skip separator line
        } else {
          i++; // no explicit separator; continue directly to rows
        }
        const splitRow = (row: string) => {
          let s = row.trim();
          if (s.startsWith('|')) s = s.slice(1);
          if (s.endsWith('|')) s = s.slice(0, -1);
          return s.split('|').map((c) => c.trim());
        };
        const headers = splitRow(headerLine).filter((c) => c.length > 0);
        let bodyRows: string[][] = [];
        while (i < lines.length) {
          const lineT = lines[i].trim();
          if (lineT === '') { i++; continue; }
          const looksLikeRow = lineT.startsWith('|') || lineT.includes('|');
          if (!looksLikeRow || isSep(lineT)) break;
          const cells = splitRow(lineT).filter((c) => c.length > 0);
          if (cells.length > 0) bodyRows.push(cells);
          i++;
        }
        // Build compact table with inline styles to avoid horizontal scrolling
        const tStyle = 'style="width:100%;table-layout:fixed;border-collapse:collapse;font-size:10px;line-height:1.25"';
        const thStyle = 'style="padding:4px;border:1px solid rgba(255,255,255,0.08);font-weight:600;text-align:left;white-space:normal;word-break:break-word"';
        const tdStyle = 'style="padding:4px;border:1px solid rgba(255,255,255,0.08);text-align:left;white-space:normal;word-break:break-word"';
        html += `<table class="mb-md-table" ${tStyle}><thead><tr>`;
        for (const h of headers) html += `<th ${thStyle}>${escapeHtml(h)}</th>`;
        html += '</tr></thead><tbody>';
        for (const row of bodyRows) {
          html += '<tr>';
          for (let c = 0; c < headers.length; c++) {
            const val = row[c] !== undefined ? row[c] : '';
            html += `<td ${tdStyle}>${escapeHtml(String(val))}</td>`;
          }
          html += '</tr>';
        }
        html += '</tbody></table>';
        continue; // don't i++ here; loop already advanced
      }

      // Simple Markdown lists (unordered and ordered)
      const isUlItem = /^\s*[-*+]\s+/.test(l);
      const isOlItem = /^\s*\d+[\.)]\s+/.test(l);
      if (isUlItem || isOlItem) {
        flushParagraph();
        const isOrdered = isOlItem;
        const tag = isOrdered ? 'ol' : 'ul';
        let items: string[] = [];
        while (i < lines.length) {
          const line = lines[i];
          const ulMatch = /^\s*[-*+]\s+/.test(line);
          const olMatch = /^\s*\d+[\.)]\s+/.test(line);
          if ((isOrdered && !olMatch) || (!isOrdered && !ulMatch)) break;
          // Extract text after marker
          const textPart = line.replace(isOrdered ? /^\s*\d+[\.)]\s+/ : /^\s*[-*+]\s+/, '');
          let itemLines: string[] = [textPart];
          // Capture following indented continuation lines
          let k = i + 1;
          while (k < lines.length && /^\s{2,}\S/.test(lines[k]) && !/^\s*[-*+]|\d+[\.)]\s+/.test(lines[k])) {
            itemLines.push(lines[k].trim());
            k++;
          }
          i = k; // advance past item and its continuations
          items.push(escapeHtml(itemLines.join('\n')).replace(/\n/g, '<br/>'));
        }
        html += `<${tag} class="mb-md-list">` + items.map((it) => `<li>${it}</li>`).join('') + `</${tag}>`;
        continue;
      }

      paragraph.push(l);
      i++;
    }
    flushParagraph();
    return html || escapeHtml(md);
  }

  function renderMessageHtml(text: string) {
    return { __html: markdownToHtml(text) };
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await PlayFab.executeCloudScript('getChatSSEConfig', {});
        if (res.success && res.data?.FunctionResult?.success) {
          const url = res.data.FunctionResult.functionUrl;
          try { console.log('[Chat] got SSE function URL from CloudScript', url); } catch {}
          setFunctionUrl(url);
        }
      } catch (e) {
        console.warn('Failed to fetch SSE Function URL via CloudScript');
      }
    })();
  }, []);

  // Fallback: allow local override via localStorage key PB_CHAT_SSE_URL for debugging
  useEffect(() => {
    if (!functionUrl) {
      const override = localStorage.getItem('PB_CHAT_SSE_URL');
      if (override && override.startsWith('https://')) {
        try { console.log('[Chat] using localStorage PB_CHAT_SSE_URL override'); } catch {}
        setFunctionUrl(override);
      }
    }
  }, [functionUrl]);

  useEffect(() => {
    const prev = prevLoadingRef.current;
    if (prev && !loading && text && text.length > 0) {
      setMessages((m) => [...m, { role: 'ai', text }]);
    }
    prevLoadingRef.current = loading;
  }, [loading, text]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const threshold = 80;
      nearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll as any); };
  }, []);

  useEffect(() => {
    // Always scroll to bottom on updates; user requested auto-scroll
    scrollToBottom();
  }, [messages, text, loading]);

  async function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }

  function estimateBase64Bytes(dataUrl: string): number {
    const idx = dataUrl.indexOf('base64,');
    if (idx < 0) return 0;
    const b64 = dataUrl.slice(idx + 7);
    const pad = (b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0));
    return Math.floor(b64.length * 3 / 4) - pad;
  }

  function loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = dataUrl;
    });
  }

  async function compressDataUrl(dataUrl: string, opts?: { maxDim?: number; quality?: number }): Promise<{ dataUrl: string; mime: string; size: number }>{
    const maxDim = opts?.maxDim ?? 1024;
    const quality = opts?.quality ?? 0.72;
    try {
      const img = await loadImage(dataUrl);
      const { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no canvas ctx');
      ctx.drawImage(img, 0, 0, targetW, targetH);
      const out = canvas.toDataURL('image/jpeg', quality);
      return { dataUrl: out, mime: 'image/jpeg', size: estimateBase64Bytes(out) };
    } catch {
      // Fallback: return original
      return { dataUrl, mime: 'image/jpeg', size: estimateBase64Bytes(dataUrl) };
    }
  }

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) { setToast('Please select an image'); setTimeout(() => setToast(null), 2000); return; }
      if (file.size > 8 * 1024 * 1024) { setToast('Image too large (max 8MB)'); setTimeout(() => setToast(null), 2000); return; }
      const rawDataUrl = await fileToDataUrl(file);
      const { dataUrl, mime, size } = await compressDataUrl(rawDataUrl, { maxDim: 1024, quality: 0.72 });
      setSelectedImage({ dataUrl, mime, size, name: file.name });
      try {
        const kb = Math.round(size / 1024);
        console.log('[Chat] image compressed to ~', kb, 'KB');
      } catch {}
    } catch (err) {
      console.error('image pick error', err);
      setToast('Failed to load image');
      setTimeout(() => setToast(null), 2000);
    }
  };

  const clearSelectedImage = () => setSelectedImage(null);

  const send = () => {
    try { console.log('[Chat] send clicked'); } catch {}
    if (!input.trim() && !selectedImage) { try { console.warn('[Chat] empty input'); } catch {}; return; }
    const question = input.trim();
    if (selectedImage) {
      setMessages((m) => [...m, { role: 'user', text: question || 'Sent a photo', imageDataUrl: selectedImage.dataUrl }]);
    } else {
      setMessages((m) => [...m, { role: 'user', text: question }]);
    }
    setInput('');
    // Ensure we jump to the newest message immediately
    scrollToBottom();
    if (!functionUrl) {
      console.error('[Chat] No SSE function URL configured');
      setToast('Chat is not configured yet');
      setTimeout(() => setToast(null), 2000);
      return;
    }
    // Send full chat history; server will truncate appropriately
    const history = messages.map((m) => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }));
    try { console.log('[Chat] preparing start', { url: functionUrl, history: history.length }); } catch {}
    const entity = PlayFab.getEntity();
    const entityId = entity && entity.Id ? entity.Id : undefined;
    const playfabId = PlayFab.getPlayFabId();
    try {
      let images: { mime: string; b64: string }[] | undefined = undefined;
      const mode = !!selectedImage ? 'meal' : undefined;
      if (selectedImage) {
        try {
          const m = selectedImage.mime || 'image/jpeg';
          const idx = selectedImage.dataUrl.indexOf('base64,');
          const b64 = idx >= 0 ? selectedImage.dataUrl.slice(idx + 7) : '';
          if (b64) images = [{ mime: m, b64 }];
        } catch {}
      }
      start(question || (selectedImage ? 'Analyze this image' : ''), { history, pf: { entityId, playfabId }, images, mode }, {});
    } catch (e) {
      console.error('[Chat] start threw', e);
      setToast('Failed to start chat');
      setTimeout(() => setToast(null), 2000);
    }
    clearSelectedImage();
  };

  const saveProgram = () => {
    if (!functionUrl) return;
    // Send full chat history; server will truncate appropriately
    const history = messages.map((m) => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }));
    const entity = PlayFab.getEntity();
    const entityId = entity && entity.Id ? entity.Id : undefined;
    const playfabId = PlayFab.getPlayFabId();
    start('Save program', { history, pf: { entityId, playfabId }, saveProgram: true }, {
      onProgram: (status) => {
        if (status === 'saved') setToast('Program saved');
        else if (status === 'skipped') setToast('Nothing to save');
        else setToast('Save failed');
        setTimeout(() => setToast(null), 2500);
      }
    });
  };

  return (
    <div className="mb-chat" style={{ minHeight: 0 }}>
      <div ref={listRef} className="mb-chat-list" style={{ alignItems: 'stretch' }}>
        {messages.map((m, idx) => (
          <div key={idx} className={`mb-bubble ${m.role === 'ai' ? 'ai' : 'user'}`}>
            {m.imageDataUrl && (
              <img src={m.imageDataUrl} alt="uploaded" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 6 }} />
            )}
            {m.role === 'ai' ? (
              <div dangerouslySetInnerHTML={renderMessageHtml(m.text)} />
            ) : (
              <div>{m.text}</div>
            )}
          </div>
        ))}
        {loading && (
          <div className={`mb-bubble ai thinking`}>
            {text ? (
              <div dangerouslySetInnerHTML={renderMessageHtml(text)} />
            ) : (
              <span className="mb-typing"><span className="mb-dot"></span><span className="mb-dot"></span><span className="mb-dot"></span></span>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="mb-chat-input" style={{ position:'sticky', bottom:0, zIndex:2, background:'rgba(0,0,0,0.5)' }}>
        <textarea
          placeholder={'Ask about macros, workouts, or form cues...'}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            try {
              const ta = e.target as HTMLTextAreaElement;
              ta.style.height = 'auto';
              ta.style.height = Math.min(120, Math.max(28, ta.scrollHeight)) + 'px';
            } catch {}
          }}
          rows={1}
        />
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPickImage} />
        <div className="mb-chat-actions">
          {loading ? (
            <button onClick={stop} className="mb-stop mb-btn-xxs" title="Stop" aria-label="Stop">
              <StopIcon size={14} />
            </button>
          ) : (
            <>
              <button onClick={() => fileInputRef.current?.click()} className="mb-btn-secondary mb-btn-xxs" title="Photo" aria-label="Photo">
                <Camera size={14} />
              </button>
              <button
                onClick={async () => {
                  try {
                    const latestAi = [...messages].reverse().find((m) => m.role === 'ai');
                    const markdown = latestAi ? latestAi.text : (text || '');
                    if (!markdown) { return; }
                    setToast('Saving...');
                    const res = await PlayFab.executeCloudScript('finalizeMealLog', { markdown });
                    const ok = res.success && res.data?.FunctionResult?.success;
                    setToast(ok ? 'Meal saved' : 'Save failed');
                    setTimeout(() => setToast(null), 2000);
                  } catch (e) {
                    console.error('Save error', e);
                    setToast('Save failed');
                    setTimeout(() => setToast(null), 2000);
                  }
                }}
                className="mb-btn-secondary mb-btn-xxs"
                title="Save meal"
                aria-label="Save meal"
              >
                <Save size={14} />
              </button>
              <button onClick={send} className="mb-btn-primary mb-btn-xxs" title="Send" aria-label="Send">
                <SendIcon size={14} />
              </button>
            </>
          )}
        </div>
      </div>
      {toast && (
        <div className="mb-card" style={{ marginTop: 8 }}>{toast}</div>
      )}
      {selectedImage && (
        <div className="mb-card" style={{ marginTop: 8 }}>
          <div className="mb-card-title">Photo attached</div>
          <img src={selectedImage.dataUrl} alt="preview" style={{ maxWidth: '100%', borderRadius: 8 }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="mb-btn-secondary" onClick={clearSelectedImage} style={{ padding: '6px 10px', fontSize: 12 }}>Remove</button>
            <button className="mb-btn-primary" onClick={send} style={{ padding: '6px 10px', fontSize: 12 }}>Send with photo</button>
          </div>
        </div>
      )}
      {/* quick action chips removed to maximize vertical space */}
    </div>
  );
}


